import { EventEmitter, inject, Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { DvDynamicFormElement, FormOf } from '@devinco/ui/legacy';
import { VehicleTypeID } from '@shared/constants/vehicle-type';

import { VehicleInformationTabForm } from './interfaces';
import { TranslateService } from '@ngx-translate/core';
import { isElectricFuelType } from '@shared/constants/fuel-types';

@Injectable({ providedIn: 'root' })
export class VehicleInfoTabFormService {
  private translate = inject(TranslateService);

  toggleEditableFieldsForNewVehicle(
    controls: FormOf<VehicleInformationTabForm>
  ): void {
    controls.DefaultUserID.enable();
    if (controls.VehicleTypeID?.value === VehicleTypeID.Corporate) {
      controls.DefaultUserID.clearValidators();
      this.triggerDefaultDriverControlStatusChange(controls);
      controls.AllowShowInMap?.setValue(true);
    } else {
      controls.DefaultUserID.addValidators([Validators.required]);
      this.triggerDefaultDriverControlStatusChange(controls);
      controls.AllowShowInMap?.setValue(false);
    }
  }

  triggerFuelConsumptionLabel(
    elements: DvDynamicFormElement[],
    fuelTypeId: number
  ): void {
    const emissionElement = elements.find(
      el => el.options.key === 'FuelConsumption'
    );

    if (!emissionElement) return;

    emissionElement.options.label = this.translate.instant(
      isElectricFuelType(fuelTypeId)
        ? 'Vehicle.FuelConsumptionForElectric'
        : 'Vehicle.FuelConsumption'
    );
  }

  triggerDefaultDriverControlStatusChange(
    controls: FormOf<VehicleInformationTabForm>
  ): void {
    const { DefaultUserID } = controls;
    (<EventEmitter<unknown>>DefaultUserID.statusChanges).emit(
      DefaultUserID.status
    );
    DefaultUserID.updateValueAndValidity();
  }

  toggleControlsForExistingVehicleType(
    vehicleTypeId: VehicleTypeID | undefined,
    controls: FormOf<VehicleInformationTabForm>,
    hasVehicleEdit: boolean
  ): void {
    const defaultDriverControl = controls.DefaultUserID;

    if (!hasVehicleEdit) {
      return;
    }

    const isCorporate = vehicleTypeId === VehicleTypeID.Corporate;
    const isPersonalWithoutDriver =
      vehicleTypeId === VehicleTypeID.Personal && !defaultDriverControl.value;

    if (isCorporate) {
      defaultDriverControl.clearValidators();
      defaultDriverControl.enable();
    } else if (isPersonalWithoutDriver) {
      defaultDriverControl.setValidators(Validators.required);
      defaultDriverControl.enable();
    } else {
      defaultDriverControl.removeValidators(Validators.required);
      defaultDriverControl.disable();
    }

    this.triggerDefaultDriverControlStatusChange({
      DefaultUserID: defaultDriverControl,
    } as FormOf<VehicleInformationTabForm>);
  }

  /** Prepare Vehicle data to match editor format */
  prepareDataForForm(
    data: VehicleInformationTabForm
  ): VehicleInformationTabForm {
    data.RegistrationDate = data.RegistrationDate?.substring(0, 10) || null;
    // Map TagDescription from string to string[]
    const tempTags = (data.TagDescription as string | null)?.split(',');
    const tags = tempTags?.filter(t => t).map(t => t.trim());
    data.TagDescription = tags || null;
    return data;
  }

  /** Prepare Vehicle data to match database */
  prepareDataForSave(
    data: Partial<VehicleInformationTabForm>
  ): Partial<VehicleInformationTabForm> {
    if (data.TagDescription) {
      data.TagDescription = (data.TagDescription as string[]).join(',');
    }

    if (data.RegistrationDate) {
      data.RegistrationDate = data.RegistrationDate
        ? data.RegistrationDate + 'T00:00:00'
        : null;
    }

    return data;
  }
}
